import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import {
  setDataScenarios,
  setDataScenariosLoading,
  setDataScenariosError,
} from 'store/dataReducers/dataScenariosSlice';
import {
  selectLevel1Data,
  selectLastFetchedPilotId,
  setLevel1Data,
  setLoading as setFeatureMetricsLoading,
  setLastFetchedPilotId
} from 'store/dataReducers/featureMetricsSlice';
import { toast } from 'utils/toast';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { useGlobalConfig } from 'context/GlobalContext';
import { createCxApiClient } from 'framework/http/CxEditorClient';
import EditColorSidebar from './EditColorSidebar';
import { PreviewArea } from 'components/wysiwyg/PreviewArea';
import { RenderRequest, Variant, DataScenario } from 'types/apis/cxEditor';
import { ColorPaletteAPI, SaveColorParams, ImpactTemplate, ColorImpactResponse } from 'apis/colorPaletteAPI';
import { ClusterApis } from 'apis/clusterAPI';
import { SECTION_ID_TO_ELEMENT_KEY_MAP } from 'utils/html-section-parser';
import { isHerTemplate } from 'utils/her-preview-url';
import { WidgetSdkProvider, useWidgetSdk } from 'context/WidgetSdkContext';
import { WidgetSdkErrorBoundary } from 'context/WidgetSdkErrorBoundary';
import { getFuelTypeFromTemplateKey } from 'apis/dataScenariosAPI';
import { normalizeFuelType } from 'utils/templates-from-feature-metrics';

const DEFAULT_VARIANT: Variant = { hierarchy: 'Project', locale: 'en_US', scenario: 'default' };
const DEFAULT_LOCALES = ['en_US'];

// Web widgets list for "View All Web Widgets" feature
const WEB_WIDGETS = [
  'DASHBOARD.HOME.BILL_PROJECTION',
  'DASHBOARD.HOME.BILL_SUMMARY',
  'DASHBOARD.HOME.BILL_ITEMIZATION',
  'DASHBOARD.ENERGY_INSIGHTS.USAGE',
  //'DASHBOARD.ENERGY_INSIGHTS.BILL_ANALYSIS'
];

/**
 * Impact `productType` may omit the trailing fuel segment (e.g. `EMAIL.DF.BILL_PROJECTION`) while
 * data-scenario rows use the full CX template name (e.g. `EMAIL.DF.BILL_PROJECTION.GAS`).
 */
function scenarioRowMatchesSelectedTemplate(
  scenarioTemplateName: string | undefined,
  selectedTemplateKey: string,
  effectiveFuelType: string
): boolean {
  if (!scenarioTemplateName) return false;
  const baseSelected = selectedTemplateKey.split('__')[0];
  if (scenarioTemplateName === baseSelected) return true;

  const fuelInSelectedKey = getFuelTypeFromTemplateKey(baseSelected);
  if (!fuelInSelectedKey) {
    return scenarioTemplateName === `${baseSelected}.${effectiveFuelType}`;
  }
  return false;
}

const parseStandardColorId = (standardColorId: string): { id: string; shade: string } => {
  const parts = standardColorId.split('_');
  
  if (parts.length === 2) {
    return { id: parts[0], shade: parts[1] };
  } else if (parts.length > 2) {
    // For complex IDs, assume first two parts are category, rest is shade
    return { id: parts.slice(0, 2).join('_'), shade: parts.slice(2).join('_') };
  }
  
  return { id: standardColorId, shade: '' };
};

const EditColorPalettePageContent: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { rc } = useGlobalConfig();
  const { standardColorId } = useParams<{ standardColorId: string }>();
  const [searchParams] = useSearchParams();
  
  // Check if readonly mode (Bidgely Standard colors)
  const isReadOnly = searchParams.get('readonly') === 'true';
  
  // Read source and template params for navigation from InspectorPanel
  const source = searchParams.get('source');
  const templateKeyParam = searchParams.get('templateKey');
  const elementKeyParam = searchParams.get('elementKey');
  const localeParam = searchParams.get('locale');
  const scenarioParam = searchParams.get('scenario');
  
  // Redux selectors
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const projectAttributes = useSelector(selectProjectAttributes);
  const level1Data = useSelector(selectLevel1Data);
  const lastFetchedPilotId = useSelector(selectLastFetchedPilotId);
  const dispatch = useDispatch();
  
  const { isReady: isSdkReady, reinitialize, instanceId } = useWidgetSdk();

  // (debug instrumentation removed)
  React.useEffect(() => {}, [standardColorId, source, templateKeyParam, elementKeyParam, localeParam, scenarioParam, isReadOnly, selectedPilotId, isSdkReady]);

  // Fetch Level 1 data when pilot changes (needed to determine fuel-type vs no-fuel templates)
  useEffect(() => {
    if (!selectedPilotId) return;

    const pilotIdStr = String(selectedPilotId);

    // Skip if we already have data for THIS specific pilot
    if (level1Data && level1Data.length > 0 && lastFetchedPilotId === pilotIdStr) {
      return;
    }

    let ignore = false;

    async function fetchLevel1Data() {
      try {
        setLevel1Loading(true);
        setLevel1Error(null);

        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);

        console.log('[ColorPalette] Fetching Level 1 feature metrics for pilot:', pilotIdStr);

        const level1Response = await api.getFeatureMetrics(pilotIdStr, 'LEVEL_1');

        if (!ignore) {
          // Validate response
          if (Array.isArray(level1Response)) {
            dispatch(setLevel1Data(level1Response));
            dispatch(setLastFetchedPilotId(pilotIdStr));
            console.log('[ColorPalette] Level 1 metrics loaded successfully:', {
              level1Count: level1Response.length,
              pilotId: pilotIdStr
            });
          } else {
            console.warn('[ColorPalette] Invalid Level 1 response format:', level1Response);
            dispatch(setLevel1Data([])); // Safe fallback
            setLevel1Error('Invalid data format received from server');
          }
          setLevel1Loading(false);
        }
      } catch (err: any) {
        if (!ignore) {
          console.error('[ColorPalette] Failed to fetch Level 1 metrics:', err);
          const errorMessage = err?.message || 'Failed to load template configuration';
          setLevel1Error(errorMessage);
          setLevel1Loading(false);

          // Set empty array to prevent infinite retry
          dispatch(setLevel1Data([]));

          // Show user-friendly toast
          toast.error(`Unable to load template configuration: ${errorMessage}`);
        }
      }
    }

    fetchLevel1Data();

    return () => {
      ignore = true;
    };
  }, [selectedPilotId, level1Data, lastFetchedPilotId, rc, dispatch]);
  
  const [projectLocales, setProjectLocales] = useState<string[]>(DEFAULT_LOCALES);
  
  // Derive fuelType from projectAttributes 
  const fuelType = useMemo(() => {
    let fuel = 'ELECTRIC';
    if (projectAttributes?.fuel_type) {
      if (typeof projectAttributes.fuel_type === 'string') {
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuel = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuel = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    return fuel;
  }, [projectAttributes]);

  // State
  const hexParam = searchParams.get('hex') || '#FFFFFF';
  const [hexValue, setHexValue] = useState(hexParam);
  const [originalHexValue, setOriginalHexValue] = useState(hexParam);
  const [impactData, setImpactData] = useState<ColorImpactResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [level1Loading, setLevel1Loading] = useState(false);
  const [level1Error, setLevel1Error] = useState<string | null>(null);
  
  // Variant state (hierarchy, locale, scenario).
  // When navigating from CX template editor, initialize from URL params so the first render uses the correct scenario
  const [variant, setVariant] = useState<Variant>(() => {
    if (typeof window === 'undefined') return DEFAULT_VARIANT;
    const params = new URLSearchParams(window.location.search);
    const src = params.get('source');
    const scen = params.get('scenario');
    const loc = params.get('locale');
    if (src === 'CXtemplateEditor' && (scen || loc)) {
      return {
        ...DEFAULT_VARIANT,
        scenario: scen || DEFAULT_VARIANT.scenario,
        locale: loc || DEFAULT_VARIANT.locale,
      };
    }
    return DEFAULT_VARIANT;
  });
  const [availableScenarios, setAvailableScenarios] = useState<Array<{ value: string; label: string }>>([
    { value: 'default', label: 'Default' }
  ]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [contentClusters, setContentClusters] = useState<Array<{ reference: string; name: string; id: string }>>([]);
  const [clustersLoading, setClustersLoading] = useState(false);
  
  // Template selection
  const [selectedTemplateComponent, setSelectedTemplateComponent] = useState<{
    templateKey: string;
    componentName: string;
    /** From color impact (EMAIL/HER); disambiguates same productType for EL vs GAS. */
    fuelType?: string;
  } | null>(null);
  const [selectedWidgets, setSelectedWidgets] = useState<string[]>([]); // For "View All Web Widgets"
  const [widgetsScenariosReady, setWidgetsScenariosReady] = useState(false); 
  const [widgetsScenariosLoading, setWidgetsScenariosLoading] = useState(false); 
  const [viewAllDashboardScenarioId, setViewAllDashboardScenarioId] = useState<string | null>(null);
  const [widgetsRenderCount, setWidgetsRenderCount] = useState(1); // Sequential rendering count for view-all widgets
  const [viewAllSdkPrimed, setViewAllSdkPrimed] = useState(false);
  const viewAllPrimingInFlightRef = useRef(false);
  const viewAllLastPrimedScenarioRef = useRef<string | null>(null);
  const [templateSidebarOpen, setTemplateSidebarOpen] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0); 
  const headerColorPickerRef = useRef<HTMLInputElement>(null);
  const sidebarAutoClosedRef = useRef(false); 

  // Derive channel from template key (HER format: HER.HER_MONTHLY_REPORT.ELECTRIC)
  const channel = useMemo(() => {
    if (selectedWidgets.length > 0) {
      return 'Web';
    }
    const templateKey = selectedTemplateComponent?.templateKey || '';
    if (isHerTemplate(templateKey)) return 'HER';
    if (templateKey.startsWith('WEB_') || templateKey.startsWith('DASHBOARD.')) return 'Web';
    return 'Email';
  }, [selectedTemplateComponent, selectedWidgets.length]);


  const getTechnicalSectionName = useCallback((componentName: string): string => {
    if (componentName === 'Full Template') {
      return 'FULL_TEMPLATE';
    }
    
    if (SECTION_ID_TO_ELEMENT_KEY_MAP[componentName]) {
      return componentName;
    }
    
    const matchingTechnicalIds: string[] = [];
    for (const [technicalId, displayName] of Object.entries(SECTION_ID_TO_ELEMENT_KEY_MAP)) {
      if (displayName === componentName) {
        matchingTechnicalIds.push(technicalId);
      }
    }
    
    if (matchingTechnicalIds.length > 0) {
      matchingTechnicalIds.sort((a, b) => {
        const versionA = parseInt(a.match(/_[Vv](\d+)/)?.[1] || '0');
        const versionB = parseInt(b.match(/_[Vv](\d+)/)?.[1] || '0');
        return versionB - versionA; 
      });
      return matchingTechnicalIds[0];
    }
    
    return componentName;
  }, []);

  useEffect(() => {
    const templateKey = selectedTemplateComponent?.templateKey;
    const componentName = selectedTemplateComponent?.componentName;
    
    if ((!templateKey && selectedWidgets.length === 0) || !selectedPilotId) {
      setAvailableScenarios([{ value: 'default', label: 'Default' }]);
      return;
    }

    // View-all is determined by selectedWidgets, not templateKey (templateKey is "DASHBOARD" for the scenario object)
    const isViewAllWebWidgets = selectedWidgets.length > 0;
    const templateKeyForFetch = templateKey || '';

    // Prefer fuel from impact selection (same productType can exist for ELECTRIC + GAS), then template key suffix, then project default.
    const effectiveFuelType = isViewAllWebWidgets
      ? fuelType
      : selectedTemplateComponent?.fuelType != null && String(selectedTemplateComponent.fuelType).trim() !== ''
        ? normalizeFuelType(selectedTemplateComponent.fuelType)
        : (getFuelTypeFromTemplateKey(templateKeyForFetch) || fuelType);

    let ignore = false;
    async function fetchScenarios() {
      setScenariosLoading(true);

      const pilotId = String(selectedPilotId);
      const locale = variant.locale || 'en_US';
      dispatch(setDataScenariosLoading({ pilotId, fuelType: effectiveFuelType, locale, loading: true }));

      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const dataScenariosResp = await api.getDataScenarios(
          pilotId,
          effectiveFuelType,
          locale
        );

        if (ignore) return;

        const dataScenarios = dataScenariosResp?.dataScenarios as DataScenario[] | undefined;

        if (dataScenarios?.length) {
          dispatch(setDataScenarios({
            pilotId,
            fuelType: effectiveFuelType,
            locale,
            dataScenarios,
          }));
        }

        let scenarioOptions: Array<{ label: string; value: string }> = [];
        let dashboardScenarioId: string | null = null;

        if (dataScenarios?.length) {
          let filteredScenarios: DataScenario[];
          
          if (isViewAllWebWidgets) {
            // For view-all widgets, the applicable scenario is:
            // productType=Web, templateName=DASHBOARD, elementNames includes FULL_TEMPLATE (cross-template widget render)
            const eligible = dataScenarios.filter((ds: DataScenario) => {
              const isWebProductType = (ds.productType || '').toLowerCase() === 'web';
              const isDashboardTemplate = String(ds.templateName || '').toUpperCase() === 'DASHBOARD';
              const elementNames = ds.elementNames || [];
              const hasFullTemplate = elementNames.includes('FULL_TEMPLATE');
              return isWebProductType && isDashboardTemplate && hasFullTemplate;
            });

            const dashboardDefault = eligible.find(ds => ds.defaultScenario === true) || eligible[0];
            dashboardScenarioId = dashboardDefault?.scenarioId || null;
            filteredScenarios = eligible;

            setViewAllDashboardScenarioId(dashboardScenarioId);
            setWidgetsRenderCount(1); // reset sequential rendering on scenario reload
          } else {
            // Match WysiwygPage: base key (strip __ suffix), DASHBOARD.* → templateName DASHBOARD, productType ↔ channel
            const baseTemplateKey = templateKeyForFetch.split('__')[0];
            const channelLower = channel.toLowerCase();

            const byTemplateAndProduct = dataScenarios.filter((ds: DataScenario) => {
              const isWebChannel = channel === 'Web';
              let matchesTemplate = scenarioRowMatchesSelectedTemplate(
                ds.templateName,
                templateKeyForFetch,
                effectiveFuelType
              );
              if (!matchesTemplate && isWebChannel && baseTemplateKey.startsWith('DASHBOARD.')) {
                matchesTemplate = ds.templateName === 'DASHBOARD';
              }
              const matchesProduct = (ds.productType || '').toLowerCase() === channelLower;
              return matchesTemplate && matchesProduct;
            });

            filteredScenarios = byTemplateAndProduct;

            if (componentName && componentName !== 'Full Template') {
              const technicalSectionName = getTechnicalSectionName(componentName);
              const withSection = filteredScenarios.filter((ds: DataScenario) => {
                const elementNames = ds.elementNames || [];
                if (elementNames.length === 0) return false;
                return elementNames.includes(technicalSectionName);
              });
              filteredScenarios = withSection.length > 0 ? withSection : byTemplateAndProduct;
            } else if (componentName === 'Full Template') {
              const withFullTemplate = filteredScenarios.filter((ds: DataScenario) => {
                const elementNames = ds.elementNames || [];
                return elementNames.includes('FULL_TEMPLATE');
              });
              filteredScenarios = withFullTemplate.length > 0 ? withFullTemplate : byTemplateAndProduct;
            }
          }
          
          scenarioOptions = filteredScenarios.map((ds: DataScenario) => ({
            label: ds.scenarioDisplayName,
            value: ds.scenarioId
          }));

          scenarioOptions.sort((a, b) => {
            const aDefault = dataScenarios.find(ds => ds.scenarioId === a.value)?.defaultScenario;
            const bDefault = dataScenarios.find(ds => ds.scenarioId === b.value)?.defaultScenario;
            if (aDefault && !bDefault) return -1;
            if (!aDefault && bDefault) return 1;
            return a.label.localeCompare(b.label);
          });
        }

        if (scenarioOptions.length === 0) {
          scenarioOptions = [{ label: 'Default', value: 'default' }];
        }

        setAvailableScenarios(scenarioOptions);
        
        if (scenarioOptions.length > 0) {
          const currentScenarioExists = scenarioOptions.some(s => s.value === variant.scenario);
          if (isViewAllWebWidgets && dashboardScenarioId) {
            // Always pin to DASHBOARD scenario if available (requested behavior)
            if (variant.scenario !== dashboardScenarioId) {
              setVariant(prev => ({ ...prev, scenario: dashboardScenarioId as string }));
            }
          } else if (!currentScenarioExists) {
            setVariant(prev => ({ ...prev, scenario: scenarioOptions[0].value }));
          }
        }
        
      } catch (error: any) {
        console.error('[EditColorPalettePage] Failed to fetch scenarios:', error);
        
        if (!ignore) {
          dispatch(setDataScenariosError({
            pilotId: String(selectedPilotId),
            fuelType: effectiveFuelType,
            locale: variant.locale || 'en_US',
            error: error?.message || 'Failed to load data scenarios',
          }));
        }
        
        setAvailableScenarios([{ value: 'default', label: 'Default' }]);
      } finally {
        if (!ignore) {
          setScenariosLoading(false);
          dispatch(setDataScenariosLoading({
            pilotId: String(selectedPilotId),
            fuelType: effectiveFuelType,
            locale: variant.locale || 'en_US',
            loading: false,
          }));
        }
      }
    }

    fetchScenarios();
    setRefreshKey(prev => prev + 1);
    
    return () => { ignore = true; };
  }, [selectedTemplateComponent?.templateKey, selectedTemplateComponent?.componentName, selectedTemplateComponent?.fuelType, selectedWidgets.length, selectedPilotId, fuelType, variant.locale, channel, rc, getTechnicalSectionName, dispatch]);

  useEffect(() => {
    if (selectedWidgets.length === 0 || !selectedPilotId) {
      setWidgetsScenariosReady(false);
      return;
    }

    let ignore = false;
    async function fetchWidgetsScenarios() {
      const pilotId = String(selectedPilotId);
      const locale = variant.locale || 'en_US';
      
      dispatch(setDataScenariosLoading({ pilotId, fuelType, locale, loading: true }));
      setWidgetsScenariosLoading(true);
      setWidgetsScenariosReady(false);
      
      try {
        const api = createCxApiClient(rc, process.env.REACT_APP_TOOL_API_URL as string);
        const dataScenariosResp = await api.getDataScenarios(
          pilotId,
          fuelType,
          locale
        );

        if (ignore) return;

        const dataScenarios = dataScenariosResp?.dataScenarios as DataScenario[] | undefined;
        

        dispatch(setDataScenarios({
          pilotId,
          fuelType,
          locale,
          dataScenarios: dataScenarios || [],
        }));
        
        setWidgetsScenariosReady(true);
      } catch (error: any) {
        if (!ignore) {
          console.error('[EditColorPalettePage] Failed to fetch widgets scenarios:', error);
          
          dispatch(setDataScenariosError({
            pilotId: String(selectedPilotId),
            fuelType,
            locale: variant.locale || 'en_US',
            error: error?.message || 'Failed to load data scenarios',
          }));
          
          setWidgetsScenariosReady(true);
        }
      } finally {
        if (!ignore) {
          const locale = variant.locale || 'en_US';
          dispatch(setDataScenariosLoading({
            pilotId: String(selectedPilotId),
            fuelType,
            locale,
            loading: false,
          }));
          setWidgetsScenariosLoading(false);
        }
      }
    }

    fetchWidgetsScenarios();
    return () => { ignore = true; };
  }, [selectedWidgets, selectedPilotId, fuelType, variant.locale, rc, dispatch]);

  // Ensure widgets SDK is initialized/re-initialized ONCE before rendering view-all widgets.
  // This prevents "redirect from cx-editor" cases where a stale module-level instanceId can cause render issues.
  React.useEffect(() => {
    if (selectedWidgets.length === 0) {
      setViewAllSdkPrimed(false);
      viewAllPrimingInFlightRef.current = false;
      viewAllLastPrimedScenarioRef.current = null;
      return;
    }
    if (!viewAllDashboardScenarioId) return;
    if (!widgetsScenariosReady) return;
    if (!isSdkReady) return;
    if (viewAllSdkPrimed && viewAllLastPrimedScenarioRef.current === viewAllDashboardScenarioId) return;
    if (viewAllPrimingInFlightRef.current) return;

    (async () => {
      const scenarioAtStart = viewAllDashboardScenarioId;
      try {
        viewAllPrimingInFlightRef.current = true;
        // Pause sequential mounting until SDK is primed.
        setWidgetsRenderCount(0);
        await reinitialize();
        // Do not cancel priming just because effect dependencies changed (reinitialize toggles isSdkReady/instanceId).
        // Only proceed if we're still in view-all mode and the scenario didn't change.
        const stillValid = selectedWidgets.length > 0 && viewAllDashboardScenarioId === scenarioAtStart;
        if (!stillValid) {
          viewAllPrimingInFlightRef.current = false;
          return;
        }
        setViewAllSdkPrimed(true);
        viewAllLastPrimedScenarioRef.current = viewAllDashboardScenarioId;
        setWidgetsRenderCount(1);
        viewAllPrimingInFlightRef.current = false;
      } catch (e: any) {
        // If reinit fails, still let user see the page, but don't spam renders.
        setViewAllSdkPrimed(false);
        setWidgetsRenderCount(0);
        viewAllPrimingInFlightRef.current = false;
      }
    })();

    return () => {
    };
  }, [selectedWidgets.length, viewAllDashboardScenarioId, widgetsScenariosReady, isSdkReady, viewAllSdkPrimed, reinitialize, instanceId]);

  const renderRequests = useMemo<RenderRequest[]>(() => {
    if (!Array.isArray(selectedWidgets) || selectedWidgets.length === 0 || !widgetsScenariosReady) {
      return [];
    }
    
    return selectedWidgets
      .filter(templateKey => templateKey && typeof templateKey === 'string')
      .map(templateKey => ({
        templateKey,
        elementKey: 'Full Template',
        previewMode: 'desktop' as const,
        variant: {
          hierarchy: variant?.hierarchy || 'Project',
          locale: variant?.locale || 'en_US',
          scenario: variant?.scenario || 'default',
        },
        // In view-all widgets mode we do NOT want refreshKey changes to trigger SDK reinitialization via PreviewFrame.
        // Keep it stable.
        refreshKey: 0,
      }));
  }, [selectedWidgets, variant, widgetsScenariosReady]);

  const handleWebWidgetFinished = useCallback((_templateKey: string, _result: { success: boolean; error?: string }) => {
    // Render widgets sequentially: after a widget finishes (success or error), allow the next one to mount.
    setWidgetsRenderCount((prev) => {
      const next = Math.min(prev + 1, renderRequests.length || prev + 1);
      return next;
    });
  }, [renderRequests.length]);

  const renderRequest = useMemo<RenderRequest | null>(() => {
    if (selectedWidgets.length > 0 || !selectedTemplateComponent?.templateKey) {
      return null;
    }
    // HER has no full-template preview; do not render when Full Template is selected
    const componentName = selectedTemplateComponent.componentName || 'Full Template';
    if (isHerTemplate(selectedTemplateComponent.templateKey) && componentName === 'Full Template') {
      return null;
    }
    // When opened from CX template editor, always use scenario/locale from URL for rendering (never fall back to 'default')
    const scenarioForRender = source === 'CXtemplateEditor' && scenarioParam ? scenarioParam : variant.scenario;
    const localeForRender = source === 'CXtemplateEditor' && localeParam ? localeParam : variant.locale;
    const previewFuel =
      selectedTemplateComponent.fuelType ??
      getFuelTypeFromTemplateKey(selectedTemplateComponent.templateKey);

    return {
      templateKey: selectedTemplateComponent.templateKey,
      elementKey: componentName,
      ...(previewFuel ? { fuelType: previewFuel } : {}),
      previewMode: 'desktop',
      variant: {
        hierarchy: variant.hierarchy,
        locale: localeForRender,
        scenario: scenarioForRender,
      },
      refreshKey,
    };
  }, [selectedTemplateComponent, selectedWidgets.length, variant, refreshKey, source, scenarioParam, localeParam]);


  // Handler for variant changes from sidebar
  const handleVariantChange = useCallback((newVariant: Partial<Variant>) => {
    setVariant(prev => ({ ...prev, ...newVariant }));
  }, []);

  const handleHeaderColorSwatchClick = () => {
    if (isReadOnly) return;
    headerColorPickerRef.current?.click();
  };

  const handleHeaderColorPickerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) return;
    const value = event.target.value.toUpperCase();
    if (/^#[0-9A-F]{6}$/i.test(value)) {
      setHexValue(value);
    }
  };

  const getCurrentProjectId = () => selectedPilotId || '';

  // Channel from URL (set by ColorPalettePage when navigating from digital/paper view); default DIGITAL for other entry points (e.g. InspectorPanel)
  const channelParam = searchParams.get('channel');
  const getCurrentChannel = useCallback((): 'DIGITAL' | 'PAPER' => {
    return channelParam === 'PAPER' ? 'PAPER' : 'DIGITAL';
  }, [channelParam]);

  // Fetch project locales from API
  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!selectedPilotId) {
        if (!ignore) {
          setProjectLocales(DEFAULT_LOCALES);
          if (!DEFAULT_LOCALES.includes(variant.locale)) {
            setVariant(prev => ({ ...prev, locale: DEFAULT_LOCALES[0] }));
          }
        }
        return;
      }
      try {
        const resp = await rc.bffClient.getProjectLocales(String(selectedPilotId));
        const list = (resp as any)?.data?.locales || DEFAULT_LOCALES;
        if (!ignore) {
          setProjectLocales(list);
          if (!list.includes(variant.locale)) {
            setVariant(prev => ({ ...prev, locale: list[0] }));
          }
        }
      } catch {
        if (!ignore) {
          setProjectLocales(DEFAULT_LOCALES);
          if (!DEFAULT_LOCALES.includes(variant.locale)) {
            setVariant(prev => ({ ...prev, locale: DEFAULT_LOCALES[0] }));
          }
        }
      }
    })();
    return () => { ignore = true; };
  }, [selectedPilotId, rc, variant.locale]);

  // Fetch CONTENT clusters: GET /v1/get-clusters/<projectId>?cluster_categories=CONTENT (called on mount and every time Hierarchy dropdown is opened)
  const fetchContentClusters = useCallback(async () => {
    if (!selectedPilotId) {
      setContentClusters([]);
      return;
    }
    setClustersLoading(true);
    try {
      const resp = await ClusterApis.getClustersList(rc, String(selectedPilotId), 'CONTENT');
      if (resp.success && resp.data) {
        const list = Array.isArray(resp.data) ? resp.data : [];
          const items = list.map((c: any) => ({
            reference: c.cluster_reference ?? c.cluster_name ?? c.name ?? String(c),
            name: c.cluster_name ?? c.name ?? c.cluster_reference ?? String(c),
            id: c.cluster_id ?? c.clusterId ?? ''
          }));
        setContentClusters(items);
      } else {
        setContentClusters([]);
      }
    } catch {
      setContentClusters([]);
    } finally {
      setClustersLoading(false);
    }
  }, [selectedPilotId, rc]);

  useEffect(() => {
    fetchContentClusters();
  }, [fetchContentClusters]);

  // Fetch impact data on mount and when channel (from URL) changes
  useEffect(() => {
    if (standardColorId) {
      fetchImpactData();
    }
  }, [standardColorId, channelParam]);

  // Auto-select template and section when navigating from CXtemplateEditor (InspectorPanel)
  // For EMAIL: uses templateKeyParam and elementKeyParam from query params
  // For WEB: renders "View All Web Widgets" by default when coming from CX template editor
  useEffect(() => {
    if (source === 'CXtemplateEditor') {
      const isWebWidget = templateKeyParam?.startsWith('DASHBOARD.') || templateKeyParam?.startsWith('WEB_');
      
      if (isWebWidget) {
        setSelectedWidgets(WEB_WIDGETS || []);
        setSelectedTemplateComponent({ 
          templateKey: 'DASHBOARD', 
          componentName: 'FULL_TEMPLATE' 
        });
        setTemplateSidebarOpen(false);
        sidebarAutoClosedRef.current = true;
        
        // Use Web_FullTemplate_1 as the universal scenario for all web widgets
        setVariant(prev => ({
          ...prev,
          locale: localeParam || prev.locale,
          scenario: 'Web_FullTemplate_1', // Fixed scenario ID for view all widgets
        }));
        return; 
      }
      
      // For EMAIL templates: Require impactData
      if (impactData?.affectedTemplates && impactData.affectedTemplates.length > 0) {
        // For EMAIL templates: Use templateKeyParam and elementKeyParam from query params
        let templateToSelect = null;
        let sectionToSelect = 'Full Template';
        
        // Match templateKey; if multiple rows share productType (EL vs GAS), use fuel from template key suffix when present.
        if (templateKeyParam) {
          const candidates = impactData.affectedTemplates.filter(
            template => template.productType === templateKeyParam
          );
          const fuelFromKey = getFuelTypeFromTemplateKey(templateKeyParam);
          templateToSelect =
            fuelFromKey && candidates.length > 1
              ? candidates.find(t => normalizeFuelType(t.fuelType) === fuelFromKey) || candidates[0]
              : candidates[0];
        }
        
        // If template not found, fall back to first template
        if (!templateToSelect) {
          templateToSelect = impactData.affectedTemplates[0];
        }
        
        // Try to find the section matching elementKeyParam
        if (elementKeyParam && elementKeyParam !== 'Full Template' && templateToSelect?.sections) {
          // Convert elementKeyParam to technical section name if needed
          const technicalSectionName = getTechnicalSectionName(elementKeyParam);
          
          // Try to find exact match first (handles both display names and technical IDs)
          let foundSection = templateToSelect.sections.find(
            section => section === elementKeyParam || section === technicalSectionName
          );
          
          // If not found, try matching by display name mapping
          if (!foundSection) {
            // Check if any section maps to the same display name
            for (const section of templateToSelect.sections) {
              const sectionDisplayName = SECTION_ID_TO_ELEMENT_KEY_MAP[section];
              if (sectionDisplayName === elementKeyParam) {
                foundSection = section;
                break;
              }
            }
          }
          
          if (foundSection) {
            sectionToSelect = foundSection;
          } else if (templateToSelect.sections.length > 0) {
            // Fall back to first section if elementKeyParam doesn't match
            sectionToSelect = templateToSelect.sections[0];
          }
        } else if (elementKeyParam === 'Full Template') {
          sectionToSelect = 'Full Template';
        } else if (templateToSelect?.sections && templateToSelect.sections.length > 0) {
          // If no elementKeyParam provided, use first section
          sectionToSelect = templateToSelect.sections[0];
        }
        
        setSelectedTemplateComponent({
          templateKey: templateToSelect.productType,
          componentName: sectionToSelect,
          fuelType: templateToSelect.fuelType
            ? normalizeFuelType(templateToSelect.fuelType)
            : undefined,
        });
      }
      
      // Also set variant if provided
      if (localeParam || scenarioParam) {
        setVariant(prev => ({
          ...prev,
          locale: localeParam || prev.locale,
          scenario: scenarioParam || prev.scenario,
        }));
      }
    }
  }, [source, impactData, templateKeyParam, elementKeyParam, localeParam, scenarioParam, getTechnicalSectionName]);

  const fetchImpactData = async () => {
    if (!standardColorId) return;

    setLoading(true);
    try {
      const { id, shade } = parseStandardColorId(standardColorId);
      const projectId = getCurrentProjectId();
      
      const response = await ColorPaletteAPI.getColorImpact(rc, {
        id,
        shade,
        projectId: projectId || undefined,
        channel: getCurrentChannel(),
        level: 'PILOT',
      });

      if (!response.success || response.error) {
        console.error('Error fetching impact data:', response.error);
        // Set empty data instead of showing error for non-critical failure
        setImpactData({
          id,
          shade,
          totalTemplates: 0,
          totalSections: 0,
          affectedTemplates: [],
        });
        return;
      }

      if (response.data) {
        setImpactData(response.data);
      }
    } catch (error) {
      console.error('Error fetching impact data:', error);
      toast.error('Failed to fetch impact data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!standardColorId || isReadOnly) return;

    if (!/^#[0-9A-F]{6}$/i.test(hexValue)) {
      toast.error('Invalid hex color format');
      return;
    }

    setSaving(true);
    try {
      const projectId = getCurrentProjectId();
      const currentChannel = getCurrentChannel();
      const { id, shade } = parseStandardColorId(standardColorId);

      const savePayload: SaveColorParams = {
        project_id: projectId || '',
        channel: currentChannel,
        id,
        shade,
        newHexValue: hexValue.toUpperCase(),
        config_level: 'pilot',
        source_name: 'cx-editor:color_palette',
      };

      const response = await ColorPaletteAPI.saveColor(rc, savePayload);

      if (!response.success || response.error) {
        toast.error(response.error || 'Failed to save color');
        return;
      }

      const versionInfo = response.data?.versionId ? ` (Version: ${response.data.versionId})` : '';
      toast.success(`Color saved successfully${versionInfo}`);
      
      setOriginalHexValue(hexValue);
      
      // Re-render widgets after save: trigger SDK re-prime for view-all, or refreshKey for single widget
      if (selectedWidgets.length > 0) {
        setViewAllSdkPrimed(false);
      } else {
        setRefreshKey(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error saving color:', error);
      toast.error('Failed to save color');
    } finally {
      setSaving(false);
    }
  };

  const navigateBack = () => {
    if (source === 'CXtemplateEditor') {
      // Pass template/section state back to WysiwygPage for maintaining state across routes
      const params = new URLSearchParams();
      if (templateKeyParam) params.set('templateKey', templateKeyParam);
      if (elementKeyParam) params.set('elementKey', elementKeyParam);
      if (localeParam) params.set('locale', localeParam);
      if (scenarioParam) params.set('scenario', scenarioParam);
      
      const queryString = params.toString();
      const path = queryString 
        ? `${ROUTE_TREE.CONTENT_MANAGEMENT.WYSIWYG.path}?${queryString}`
        : ROUTE_TREE.CONTENT_MANAGEMENT.WYSIWYG.path;
      navigate(path, { replace: true });
    } else {
      const params = new URLSearchParams();
      params.set('channel', channelParam === 'PAPER' ? 'PAPER' : 'DIGITAL');
      navigate(`${ROUTE_TREE.CONTENT_MANAGEMENT.COLOR_PALETTE.path}?${params.toString()}`, { replace: true });
    }
  };

  const handleBack = () => {
    if (!isReadOnly && hexValue !== originalHexValue) {
      if (window.confirm('You have unsaved changes. Are you sure you want to go back?')) {
        navigateBack();
      }
    } else {
      navigateBack();
    }
  };

  const handleHexChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) return;
    let value = event.target.value.toUpperCase();
    if (value && !value.startsWith('#')) {
      value = '#' + value;
    }
    if (/^#[0-9A-F]{0,6}$/.test(value)) {
      setHexValue(value);
    }
  };

  const handleHexBlur = () => {
    if (!/^#[0-9A-F]{6}$/i.test(hexValue)) {
      setHexValue(originalHexValue);
    }
  };

  const handleComponentSelect = (templateKey: string, componentName: string, templateFuelType?: string) => {
    if (!templateKey) return;
    
    if (templateKey === 'VIEW_ALL_WEB_WIDGETS') {
      setSelectedWidgets(WEB_WIDGETS || []);
      // Align selection with the scenario object: templateName=DASHBOARD, elementNames includes FULL_TEMPLATE
      setSelectedTemplateComponent({ templateKey: 'DASHBOARD', componentName: 'FULL_TEMPLATE' }); 
      setTemplateSidebarOpen(false);
      sidebarAutoClosedRef.current = true;
    } else {
      setSelectedWidgets([]); 
      setSelectedTemplateComponent({
        templateKey,
        componentName: componentName || 'Full Template',
        ...(templateFuelType != null && String(templateFuelType).trim() !== ''
          ? { fuelType: normalizeFuelType(templateFuelType) }
          : {}),
      });
      if (templateKey && typeof templateKey === 'string' && templateKey.startsWith('DASHBOARD.')) {
        setTemplateSidebarOpen(false);
        sidebarAutoClosedRef.current = true; 
      }
    }
  };
  
  const handleToggleSidebar = useCallback(() => {
    setTemplateSidebarOpen(prev => {
      const newState = !prev;
      // When user manually opens sidebar, mark that it was manually opened
      if (newState === true && Array.isArray(selectedWidgets) && selectedWidgets.length > 0) {
        sidebarAutoClosedRef.current = true; // Set to true to prevent auto-close
      }
      return newState;
    });
  }, [selectedWidgets.length]);
  
  useEffect(() => {
    if (Array.isArray(selectedWidgets) && selectedWidgets.length > 0 && templateSidebarOpen && !sidebarAutoClosedRef.current) {
      sidebarAutoClosedRef.current = true;
      setTemplateSidebarOpen(false);
    }
    
    if (!Array.isArray(selectedWidgets) || selectedWidgets.length === 0) {
      sidebarAutoClosedRef.current = false;
    }
  }, [selectedWidgets.length, templateSidebarOpen]); 

  const getDisplayName = (colorId: string) => {
    return colorId
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const isValidHex = /^#[0-9A-F]{6}$/i.test(hexValue);
  const hasChanges = hexValue !== originalHexValue;
  const isSaveDisabled = saving || !hasChanges || !isValidHex || isReadOnly;

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh', py: 4 }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {!isReadOnly && (
              <input
                ref={headerColorPickerRef}
                type="color"
                value={hexValue}
                onChange={handleHeaderColorPickerChange}
                style={{
                  position: 'absolute',
                  opacity: 0,
                  width: 0,
                  height: 0,
                  pointerEvents: 'none',
                }}
              />
            )}
            <Tooltip title={isReadOnly ? hexValue : "Click to open color picker"} placement="bottom">
              <Box
                onClick={handleHeaderColorSwatchClick}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: hexValue,
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                  cursor: isReadOnly ? 'default' : 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': isReadOnly ? {} : {
                    transform: 'scale(1.05)',
                    boxShadow: 2,
                  },
                }}
              />
            </Tooltip>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 800, 
                color: theme.palette.text.primary,
                textAlign: 'left',
              }}
            >
              {isReadOnly ? 'View' : 'Edit'} Color: {standardColorId ? getDisplayName(standardColorId) : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                textTransform: 'none',
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
              }}
            >
              Back
            </Button>
            {!isReadOnly && (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSaveDisabled}
                sx={{
                  textTransform: 'none',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    opacity: 0.5,
                  },
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, height: 'calc(100vh - 200px)' }}>
          {/* Left Sidebar */}
          <EditColorSidebar
            hexValue={hexValue}
            onHexChange={handleHexChange}
            onHexBlur={handleHexBlur}
            templates={impactData?.affectedTemplates || []}
            level1Data={level1Data || []}
            level1Loading={level1Loading}
            level1Error={level1Error}
            loading={loading}
            onComponentSelect={handleComponentSelect}
            selectedComponent={selectedTemplateComponent}
            variant={variant}
            onVariantChange={handleVariantChange}
            availableLocales={projectLocales}
            availableScenarios={availableScenarios}
            scenariosLoading={scenariosLoading}
            isOpen={templateSidebarOpen}
            onToggleOpen={handleToggleSidebar}
            isReadOnly={isReadOnly}
            isSdkReady={isSdkReady}
            channel={channelParam === 'PAPER' ? 'PAPER' : 'DIGITAL'}
            availableClusters={contentClusters}
            clustersLoading={clustersLoading}
            projectId={selectedPilotId ? String(selectedPilotId) : undefined}
            onClustersDropdownOpen={fetchContentClusters}
          />

          {/* Right Main Content - Template Preview */}
          <Box sx={{ flex: 1, height: '100%', overflow: 'hidden' }}>
            {selectedWidgets.length > 0 ? (
              widgetsScenariosLoading || !widgetsScenariosReady ? (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed',
                    borderColor: theme.palette.divider,
                    borderRadius: 2,
                    bgcolor: theme.palette.mode === 'dark' 
                      ? theme.palette.background.paper 
                      : theme.palette.grey[50],
                  }}
                >
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {widgetsScenariosLoading ? 'Loading scenarios...' : 'Preparing widgets...'}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    overflow: 'auto',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  }}
                >
                  {!viewAllDashboardScenarioId ? (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="error">
                        No DASHBOARD scenario found for Web widgets. Please configure a Web scenario with templateName = DASHBOARD and FULL_TEMPLATE.
                      </Typography>
                    </Box>
                  ) : (!viewAllSdkPrimed ? (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Initializing widget SDK…
                      </Typography>
                    </Box>
                  ) : renderRequests.slice(0, widgetsRenderCount).map((req, index) => (
                    <Box key={`${req.templateKey}-${index}`} sx={{ flexShrink: 0 }}>
                      <PreviewArea
                        request={req}
                        previewMode="desktop"
                        channel="Web"
                        variant={variant}
                        isFullTemplateSelected={true}
                        onWebWidgetFinished={handleWebWidgetFinished}
                      />
                    </Box>
                  )))}
                </Box>
              )
            ) : renderRequest ? (
              <PreviewArea
                request={renderRequest}
                previewMode="desktop"
                channel={channel}
                isFullTemplateSelected={selectedTemplateComponent?.componentName === 'Full Template'}
                projectId={selectedPilotId}
                elementKey={selectedTemplateComponent?.componentName || 'Full Template'}
              />
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed',
                  borderColor: theme.palette.divider,
                  borderRadius: 2,
                  bgcolor: theme.palette.mode === 'dark'
                    ? theme.palette.background.paper
                    : theme.palette.grey[50],
                  px: 2,
                }}
              >
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}>
                  {channel === 'HER' && (selectedTemplateComponent?.componentName === 'Full Template' || !selectedTemplateComponent?.componentName) ? (
                    <>Full Template preview is not available for HER. Please select a section from the sidebar.</>
                  ) : (
                    <>Select a template component to preview</>
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const EditColorPalettePage: React.FC = () => {
  return (
    <WidgetSdkErrorBoundary>
      <WidgetSdkProvider>
        <EditColorPalettePageContent />
      </WidgetSdkProvider>
    </WidgetSdkErrorBoundary>
  );
};

export default EditColorPalettePage;
